import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Package, PackageCheck, Phone, Send, Store, Truck } from 'lucide-react';
import api, { getErrorMessage } from '@/services/api';
import { ApiResponse, OnlineOrder } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants';
import { Alert, Avatar, Badge, Button, Card, IconButton, IconTile, LoadingSkeleton, SummaryLine } from '@/components/ui';
import { PageShell } from '@/components/layout/PageShell';
import { formatCurrency, formatDateTime } from '@/utils/formatters';
import { BarcodeScannerModal, hasBarcodeDetector } from '@/features/catalog/BarcodeScannerModal';
import { OrderActionSheet, isPickup, useOnlineOrderActions } from './useOnlineOrderActions';

interface Props {
    shopId: number;
    transactionId: string;
    onClose: () => void;
    onChanged: () => void;
}

const statusTone = (s: string): 'warning' | 'brand' | 'info' | 'success' | 'danger' | 'neutral' =>
    s === 'PAID' ? 'warning' : s === 'READY' ? 'brand' : s === 'SENT' ? 'info' : s === 'DELIVERED' || s === 'DONE' ? 'success' : s === 'VOID' || s === 'REFUNDED' ? 'danger' : 'neutral';

/** 12b Detail pesanan online — items | customer + courier, stage action in the footer. */
export const OnlineOrderDetail: React.FC<Props> = ({ shopId, transactionId, onClose, onChanged }) => {
    const { t } = useTranslation();
    const [order, setOrder] = useState<OnlineOrder | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [scan, setScan] = useState(false);
    const actions = useOnlineOrderActions(shopId, (updated) => { setOrder(updated); onChanged(); });

    useEffect(() => {
        setLoading(true);
        setError(null);
        api.get<ApiResponse<OnlineOrder>>(API_ENDPOINTS.ONLINE_ORDER(shopId, transactionId))
            .then((r) => setOrder(r.data.data))
            .catch((e) => setError(getErrorMessage(e)))
            .finally(() => setLoading(false));
    }, [shopId, transactionId]);

    const items = order ? actions.actionsFor(order) : [];
    const primary = items[0];
    const addr = order ? [order.address, order.subdistrict?.subdistrictName, order.city && `${order.city.cityType ?? ''} ${order.city.cityName ?? ''}`.trim(), order.province?.provinceName, order.city?.postalCode].filter(Boolean).join(', ') : '';
    const pickup = order ? isPickup(order) : false;
    const PrimaryIcon = primary && 'status' in primary.action && primary.action.status === 'SENT' ? Send : PackageCheck;

    return (
        <PageShell
            title={`#${transactionId}`}
            subtitle={order ? `${formatDateTime(order.createdAt)} · ${pickup ? t('onlineOrders.pickup') : t('onlineOrders.courier')}` : undefined}
            onBack={onClose}
            actions={order && <Badge tone={statusTone(order.transactionStatus)} dot>{t(`onlineOrders.status.${order.transactionStatus}` as 'onlineOrders.status.PAID', { defaultValue: order.transactionStatus })}</Badge>}
            footer={order && primary && (
                <div className="flex items-center gap-3">
                    <span className="hidden flex-1 text-[13px] text-ink-muted lg:block">{order.transactionStatus === 'PAID' ? t('onlineOrders.afterReadyHint') : ''}</span>
                    <Button variant="primary" size="lg" fullWidth icon={<PrimaryIcon />} onClick={() => actions.request(order, primary.action)} className="lg:!w-auto lg:!flex-none lg:px-8">{primary.longLabel ?? primary.label}</Button>
                </div>
            )}
        >
            <div className="flex flex-col gap-3 pb-6 lg:grid lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:gap-4">
                {loading && <><LoadingSkeleton height={160} /><LoadingSkeleton height={160} /></>}
                {error && <Alert variant="error" message={error} />}
                {order && !loading && (
                    <>
                        <Card padding="md" className="flex flex-col !rounded-xl lg:p-[20px_22px]">
                            <span className="pb-1.5 text-base font-extrabold">{t('onlineOrders.items')}</span>
                            {order.transactionsItems.map((it) => (
                                <div key={it.id} className="flex items-center gap-3.5 border-b border-line-subtle py-3 last:border-b-0">
                                    <IconTile size={48} tone="sunken" src={it.products?.image}><Package /></IconTile>
                                    <span className="flex min-w-0 flex-1 flex-col"><span className="truncate text-sm font-extrabold">{it.products?.title ?? '-'}</span><span className="text-xs text-ink-muted tnum">{it.products?.subtitle ? `${it.products.subtitle} · ` : ''}{it.quantity} × {formatCurrency(it.endPrice)}{it.products?.unit ? ` ${it.products.unit}` : ''}</span></span>
                                    <span className="text-sm font-black tnum">{formatCurrency(it.endPrice * it.quantity)}</span>
                                </div>
                            ))}
                            <div className="hidden flex-1 lg:block" />
                            <div className="flex flex-col gap-2 border-t border-line-subtle pt-3">
                                <SummaryLine label={t('cart.subtotal')} value={formatCurrency(order.subTotal)} />
                                <SummaryLine label={t('onlineOrders.shippingCost')} value={formatCurrency(order.shippingCost)} />
                                <SummaryLine kind="total" large label={t('cart.total')} value={formatCurrency(order.total)} />
                            </div>
                        </Card>

                        <div className="flex flex-col gap-3 lg:gap-4">
                            <Card padding="md" className="flex flex-col gap-3 !rounded-xl lg:p-[20px_22px]">
                                <span className="text-base font-extrabold">{t('onlineOrders.customer')}</span>
                                <div className="flex items-center gap-3">
                                    <Avatar name={order.recipientName || order.users?.fullname} size="md" />
                                    <span className="flex min-w-0 flex-1 flex-col"><span className="truncate text-sm font-extrabold">{order.recipientName || order.users?.fullname || '-'}</span><span className="text-xs text-ink-muted tnum">{order.recipientPhone || order.users?.phone || ''}</span></span>
                                    {(order.recipientPhone || order.users?.phone) && <IconButton variant="sunken" size="xs" label={t('cart.customerPhone')} onClick={() => window.open(`tel:${order.recipientPhone || order.users?.phone}`)} className="!text-ink-brand"><Phone /></IconButton>}
                                </div>
                                {addr && <p className="flex gap-2.5 text-[13px] leading-normal text-ink-secondary"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" />{addr}</p>}
                                {order.users?.email && <p className="text-xs text-ink-muted">{order.users.email}</p>}
                            </Card>
                            <Card padding="md" className="flex flex-col gap-3 !rounded-xl lg:flex-1 lg:p-[20px_22px]">
                                <span className="text-base font-extrabold">{pickup ? t('onlineOrders.pickup') : t('onlineOrders.courier')}</span>
                                <div className="flex items-center gap-3">
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brandsoft text-ink-brand">{pickup ? <Store className="h-5 w-5" /> : <Truck className="h-5 w-5" />}</span>
                                    <span className="flex min-w-0 flex-1 flex-col">
                                        <span className="truncate text-sm font-extrabold">{[order.courierName, order.courierService].filter(Boolean).join(' ') || (pickup ? t('onlineOrders.pickup') : '-')}</span>
                                        <span className="text-xs text-ink-muted">{order.etd ? t('onlineOrders.etd', { etd: order.etd }) : order.paymentMethod ?? ''}</span>
                                    </span>
                                </div>
                                {order.airwaybill && <p className="text-xs text-ink-secondary">AWB <span className="font-extrabold tnum">{order.airwaybill}</span></p>}
                                {(order.note || order.courierNotes) && <p className="rounded-md bg-surface-sunken px-3 py-2.5 text-[13px] text-ink-secondary">{t('onlineOrders.note')}: {order.note || order.courierNotes}</p>}
                            </Card>
                        </div>
                    </>
                )}
            </div>
            <OrderActionSheet actions={actions} onScan={hasBarcodeDetector() ? () => setScan(true) : undefined} />
            <BarcodeScannerModal isOpen={scan} continuous={false} onClose={() => setScan(false)} onDetected={(code) => { actions.setAirwaybill(code); setScan(false); }} />
        </PageShell>
    );
};

export default OnlineOrderDetail;
